import { useEffect, useState } from "react";
import { supabase } from "../lib/supabaseClient";
import { ACCOUNT_SYNC_EVENT } from "../lib/accountStorage";
import "../google-one-tap.css";

// Public web client ID from the existing Google OAuth provider, not a secret.
const CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID || "524550431245-8c97sei5gd99ng8203g3gbc3nbsvt4ab.apps.googleusercontent.com";
const ATTEMPT_KEY = "gwrene_one_tap_offered";
let scriptPromise;

function loadGoogle() {
  if (window.google?.accounts?.id) return Promise.resolve(window.google.accounts.id);
  if (!scriptPromise) {
    scriptPromise = new Promise((resolve, reject) => {
      const script = document.createElement("script");
      script.src = "https://accounts.google.com/gsi/client";
      script.async = true;
      script.onload = () => resolve(window.google.accounts.id);
      script.onerror = () => {
        script.remove();
        scriptPromise = undefined;
        reject(new Error("Google sign-in could not load."));
      };
      document.head.appendChild(script);
    });
  }
  return scriptPromise;
}

function hasPracticeWin() {
  try {
    const progress = JSON.parse(localStorage.getItem("gwrene_day_progress") || "{}");
    return Object.values(progress).some((done) => done === true);
  } catch {
    return false;
  }
}

export default function GoogleOneTap() {
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!supabase || !window.isSecureContext) return undefined;
    let active = true;
    let signedIn = false;
    let offered = false;
    let interacting = false;
    let google;
    let timer;

    function rememberOffer() {
      try { sessionStorage.setItem(ATTEMPT_KEY, "1"); } catch { /* Optional storage. */ }
    }

    async function offer() {
      const path = window.location.pathname;
      const accountPage = /^\/account\/?$/.test(path);
      const purchasePage = /^\/(pro|members-pro)\/?$/.test(path);
      const practicePage = path === "/" || /^\/members?(\/|$)/.test(path);
      // Let new learners reach a result before asking for an account.
      if (!(accountPage || purchasePage || (practicePage && hasPracticeWin()))) return;
      if (!active || offered || signedIn || interacting || document.hidden) return;
      if (document.body.classList.contains("cookie-banner-open") || document.querySelector('[role="dialog"][aria-modal="true"]')) return;
      const params = new URLSearchParams(window.location.search);
      if (/access_token|refresh_token|error=/.test(window.location.hash) || params.has("code") || params.has("token_hash")) return;
      try {
        let alreadyOffered = false;
        try { alreadyOffered = Boolean(sessionStorage.getItem(ATTEMPT_KEY)); } catch { /* Optional storage. */ }
        if (alreadyOffered) return;
        offered = true;
        const { data, error } = await supabase.auth.getSession();
        if (!active || error || data.session || signedIn || interacting) return;
        google = await loadGoogle();
        const nonce = Array.from(crypto.getRandomValues(new Uint8Array(32)), (byte) => byte.toString(16).padStart(2, "0")).join("");
        const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(nonce));
        const hashedNonce = Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, "0")).join("");
        if (!active || signedIn || interacting) return;
        google.initialize({
          client_id: CLIENT_ID,
          nonce: hashedNonce,
          auto_select: false,
          cancel_on_tap_outside: true,
          callback: async ({ credential }) => {
            if (!active || signedIn || interacting || !credential) return;
            try {
              const { error: signInError } = await supabase.auth.signInWithIdToken({ provider: "google", token: credential, nonce });
              if (!active) return;
              if (signInError) { setFailed(true); return; }
              // Account sign-in follows the existing /member checkout-resume path.
              // Elsewhere keep the learner on their current lesson or offer.
              if (/^\/account\/?$/.test(window.location.pathname)) window.location.assign("/member");
            } catch {
              if (active) setFailed(true);
            }
          },
        });
        rememberOffer();
        google.prompt();
      } catch {
        // Blocked scripts and unsupported browsers retain Google OAuth + email.
      }
    }

    function schedule() {
      window.clearTimeout(timer);
      timer = window.setTimeout(offer, 6000);
    }
    function onFocus(event) {
      if (event.target.closest("input, textarea, [contenteditable=true]")) {
        interacting = true;
        google?.cancel();
      }
    }
    function onSignInClick(event) {
      if (event.target.closest(".account-google-btn, .account-magic-form button")) {
        interacting = true;
        google?.cancel();
      }
    }
    const { data: authListener } = supabase.auth.onAuthStateChange((event, session) => {
      signedIn = Boolean(session);
      if (signedIn || event === "SIGNED_OUT") {
        if (event === "SIGNED_OUT") { offered = true; interacting = true; rememberOffer(); google?.disableAutoSelect(); }
        google?.cancel();
      }
    });
    document.addEventListener("focusin", onFocus);
    document.addEventListener("click", onSignInClick);
    document.addEventListener("visibilitychange", schedule);
    // CookieConsent opens at four seconds on short pages. Retry after it closes
    // instead of letting that first overlap suppress One Tap for the whole visit.
    const consentObserver = new MutationObserver(() => {
      if (!document.body.classList.contains("cookie-banner-open")) schedule();
    });
    consentObserver.observe(document.body, { attributes: true, attributeFilter: ["class"] });
    window.addEventListener(ACCOUNT_SYNC_EVENT, schedule);
    window.addEventListener("popstate", schedule);
    schedule();
    return () => {
      active = false;
      window.clearTimeout(timer);
      document.removeEventListener("focusin", onFocus);
      document.removeEventListener("click", onSignInClick);
      document.removeEventListener("visibilitychange", schedule);
      consentObserver.disconnect();
      window.removeEventListener(ACCOUNT_SYNC_EVENT, schedule);
      window.removeEventListener("popstate", schedule);
      authListener.subscription.unsubscribe();
      google?.cancel();
    };
  }, []);

  return failed ? (
    <div className="gwr-one-tap-error" role="status">
      Google sign-in didn&apos;t finish. <a href="/account">Try Google or an email link</a>.
      <button type="button" onClick={() => setFailed(false)} aria-label="Dismiss sign-in message">×</button>
    </div>
  ) : null;
}
